import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-no-result',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './no-result.component.html',
  styleUrl: './no-result.component.scss'
})
export class NoResultComponent implements OnInit, OnDestroy {
  canvas!: HTMLCanvasElement;
  ctx!: CanvasRenderingContext2D;

  // Game state
  gameRunning = false;
  score = 0;
  gameOver = false;

  // Player
  player = {
    x: 50,
    y: 200,
    width: 40,
    height: 40,
    velocityY: 0,
    jumping: false
  };

  // Obstacles
  obstacles: Array<{ x: number; y: number; width: number; height: number }> = [];
  obstacleTimer = 0;

  // Game loop
  private animationId: number = 0;
  private gravity = 0.6;
  private jumpPower = -12;
  private groundY = 200;

  ngOnInit() {
    setTimeout(() => this.initGame(), 100);
  }

  ngOnDestroy() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyPress(event: KeyboardEvent) {
    if (event.code === 'Space') {
      event.preventDefault();
      if (!this.gameRunning && !this.gameOver) {
        this.startGame();
      } else if (this.gameOver) {
        this.resetGame();
      } else {
        this.jump();
      }
    }
  }

  initGame() {
    this.canvas = document.getElementById('gameCanvas') as HTMLCanvasElement;
    if (this.canvas) {
      this.ctx = this.canvas.getContext('2d')!;
      this.draw();
    }
  }

  startGame() {
    this.gameRunning = true;
    this.gameOver = false;
    this.score = 0;
    this.obstacles = [];
    this.obstacleTimer = 0;
    this.gameLoop();
  }

  resetGame() {
    this.player.y = this.groundY;
    this.player.velocityY = 0;
    this.player.jumping = false;
    this.gameOver = false;
    this.score = 0;
    this.obstacles = [];
    this.obstacleTimer = 0;
    this.startGame();
  }

  jump() {
    if (!this.player.jumping) {
      this.player.velocityY = this.jumpPower;
      this.player.jumping = true;
    }
  }

  gameLoop() {
    this.update();
    this.draw();

    if (!this.gameOver) {
      this.animationId = requestAnimationFrame(() => this.gameLoop());
    }
  }

  update() {
    // Update player physics
    this.player.velocityY += this.gravity;
    this.player.y += this.player.velocityY;

    // Ground collision
    if (this.player.y >= this.groundY) {
      this.player.y = this.groundY;
      this.player.velocityY = 0;
      this.player.jumping = false;
    }

    // Generate obstacles with varying sizes
    this.obstacleTimer++;
    if (this.obstacleTimer > 90) {
      const randomHeight = 35 + Math.random() * 15; // Random height between 35-50
      const randomWidth = 25 + Math.random() * 10;  // Random width between 25-35

      this.obstacles.push({
        x: 800,
        y: this.groundY + this.player.height - randomHeight,
        width: randomWidth,
        height: randomHeight
      });
      this.obstacleTimer = 0;
    }

    // Update obstacles
    for (let i = this.obstacles.length - 1; i >= 0; i--) {
      this.obstacles[i].x -= 6; // Slightly faster

      // Remove off-screen obstacles
      if (this.obstacles[i].x + this.obstacles[i].width < 0) {
        this.obstacles.splice(i, 1);
        this.score += 10;
        continue;
      }

      // Collision detection (adjusted for stick figure)
      const playerBounds = {
        x: this.player.x + 5,
        y: this.player.y - this.player.height,
        width: this.player.width - 10,
        height: this.player.height
      };

      if (this.checkCollision(playerBounds, this.obstacles[i])) {
        this.gameOver = true;
        this.gameRunning = false;
      }
    }
  }

  checkCollision(rect1: any, rect2: any): boolean {
    return rect1.x < rect2.x + rect2.width &&
           rect1.x + rect1.width > rect2.x &&
           rect1.y < rect2.y + rect2.height &&
           rect1.y + rect1.height > rect2.y;
  }

  drawStickFigure(x: number, y: number, size: number) {
    const ctx = this.ctx;

    // Head
    ctx.beginPath();
    ctx.arc(x + size / 2, y - size * 1.3, size / 3, 0, Math.PI * 2);
    ctx.fillStyle = '#FFE4B5';
    ctx.fill();
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Body (torso)
    ctx.beginPath();
    ctx.moveTo(x + size / 2, y - size);
    ctx.lineTo(x + size / 2, y - size / 3);
    ctx.stroke();

    // Arms
    ctx.beginPath();
    ctx.moveTo(x + size / 2, y - size * 0.8);
    ctx.lineTo(x + size * 0.2, y - size * 0.5);
    ctx.moveTo(x + size / 2, y - size * 0.8);
    ctx.lineTo(x + size * 0.8, y - size * 0.5);
    ctx.stroke();

    // Legs
    ctx.beginPath();
    ctx.moveTo(x + size / 2, y - size / 3);
    ctx.lineTo(x + size * 0.3, y);
    ctx.moveTo(x + size / 2, y - size / 3);
    ctx.lineTo(x + size * 0.7, y);
    ctx.stroke();
  }

  drawCactus(x: number, y: number, width: number, height: number) {
    const ctx = this.ctx;

    // Main stem
    ctx.fillStyle = '#2E7D32';
    ctx.fillRect(x + width / 4, y, width / 2, height);

    // Left arm
    ctx.fillRect(x, y + height / 3, width / 4, height / 3);
    ctx.fillRect(x, y + height / 3, width / 2, width / 4);

    // Right arm
    ctx.fillRect(x + width * 0.75, y + height / 2, width / 4, height / 4);
    ctx.fillRect(x + width / 2, y + height / 2, width / 2, width / 4);

    // Outline
    ctx.strokeStyle = '#1B5E20';
    ctx.lineWidth = 2;
    ctx.strokeRect(x + width / 4, y, width / 2, height);
  }

  drawRock(x: number, y: number, width: number, height: number) {
    const ctx = this.ctx;

    // Rock shape (irregular polygon)
    ctx.beginPath();
    ctx.moveTo(x + width * 0.2, y + height);
    ctx.lineTo(x, y + height * 0.6);
    ctx.lineTo(x + width * 0.3, y);
    ctx.lineTo(x + width * 0.7, y + height * 0.2);
    ctx.lineTo(x + width, y + height * 0.5);
    ctx.lineTo(x + width * 0.8, y + height);
    ctx.closePath();

    ctx.fillStyle = '#757575';
    ctx.fill();
    ctx.strokeStyle = '#424242';
    ctx.lineWidth = 2;
    ctx.stroke();
  }

  draw() {
    // Clear canvas
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Draw sky gradient
    const gradient = this.ctx.createLinearGradient(0, 0, 0, this.canvas.height);
    gradient.addColorStop(0, '#87CEEB');
    gradient.addColorStop(1, '#E0F6FF');
    this.ctx.fillStyle = gradient;
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Draw ground
    this.ctx.fillStyle = '#D2B48C';
    this.ctx.fillRect(0, this.groundY + this.player.height, this.canvas.width, this.canvas.height);

    // Draw ground line
    this.ctx.strokeStyle = '#8B7355';
    this.ctx.lineWidth = 3;
    this.ctx.beginPath();
    this.ctx.moveTo(0, this.groundY + this.player.height);
    this.ctx.lineTo(this.canvas.width, this.groundY + this.player.height);
    this.ctx.stroke();

    // Draw stick figure player
    this.drawStickFigure(this.player.x, this.player.y + this.player.height, this.player.height);

    // Draw obstacles (alternate between cactus and rocks)
    this.obstacles.forEach((obstacle, index) => {
      if (index % 2 === 0) {
        this.drawCactus(obstacle.x, obstacle.y, obstacle.width, obstacle.height);
      } else {
        this.drawRock(obstacle.x, obstacle.y, obstacle.width, obstacle.height);
      }
    });

    // Draw score
    this.ctx.fillStyle = '#000';
    this.ctx.font = 'bold 24px Arial';
    this.ctx.fillText(`Score: ${this.score}`, 10, 35);

    // Draw game over text
    if (this.gameOver) {
      // Semi-transparent overlay
      this.ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
      this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

      // Game over text
      this.ctx.fillStyle = '#FFF';
      this.ctx.font = 'bold 40px Arial';
      this.ctx.textAlign = 'center';
      this.ctx.fillText('Game Over!', this.canvas.width / 2, this.canvas.height / 2 - 20);

      this.ctx.font = '20px Arial';
      this.ctx.fillText(`Final Score: ${this.score}`, this.canvas.width / 2, this.canvas.height / 2 + 20);

      this.ctx.font = '18px Arial';
      this.ctx.fillText('Press SPACE to restart', this.canvas.width / 2, this.canvas.height / 2 + 60);

      this.ctx.textAlign = 'left';
    }
  }

  onCanvasClick() {
    if (!this.gameRunning && !this.gameOver) {
      this.startGame();
    } else if (this.gameOver) {
      this.resetGame();
    } else {
      this.jump();
    }
  }
}
